import { GeneralProposalEntity } from "domain/entity/GeneralProposal/GeneralProposalEntity";
import { AclType } from "presentation/constant/ANAInfo/NbisivPermission";
import { Permission } from "presentation/constant/ANAInfo/PermissionName";
import { GeneralProposalConstant } from "presentation/constant/GeneralProposal/GeneralProposalConstant";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useGeneralProposalVM } from "presentation/hook/GeneralProposal/useGeneralProposalVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useGeneralProposalTracked } from "presentation/store/GeneralProposal/GeneralProposalProvider";
import { isPermissionExist } from "presentation/utils/permissionUtils";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import { NbisIconButton } from "presentation/view/components/NbisIconButton";
import { memo, useCallback, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { Sidebarheader, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const GeneralProposalTitleBar: React.FC = () => {
    const [generalProposalState] = useGeneralProposalTracked();
    const generalProposalVM = useGeneralProposalVM();
    const { selectedRows, searchCriteria, searchStatus, isShowEditPanel } = generalProposalState;
    const { isRead } = generalProposalState.gnProposalState;
    const GENERAL_PROPOSAL_CONSTANT = GeneralProposalConstant.Title;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowCreate, allowUpdate, allowDelete } = anaInfoState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);
    const [deletedProposal, setDeletedProposal] = useState<GeneralProposalEntity[]>();
    const allowUpdateReject = isPermissionExist(Permission.GENERAL_PROPOSAL_REJECT, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateUnconfirm = isPermissionExist(Permission.GENERAL_PROPOSAL_UNCONFIRM, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);
    const allowUpdateApprove = isPermissionExist(Permission.GENERAL_PROPOSAL_APPROVE, AclType.UPDATE, anaInfoState.defaultOperatingCompany, anaInfoState.allNbisivPermission);

    const handleSearch = useCallback(() => {
        generalProposalVM.onSearchClick();
    }, [generalProposalVM]);

    const handleAdd = useCallback(() => {
        generalProposalVM.onAddClick();
    }, [generalProposalVM]);

    const handleReject = useCallback(async () => {
        if (selectedRows.length !== 1) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        generalProposalVM.onRejectClick();

    }, [messageBarVM, selectedRows, generalProposalVM]);

    const handleApprove = useCallback(async () => {
        if (anaInfoState.operatingUnit === 'SAPT' || anaInfoState.operatingUnit === 'KICT') {
            let isError = false
            selectedRows?.forEach(entity => {
                if (entity.submittedBy === anaInfoState.userName) {
                    messageBarVM.showError("Proposal cannot approve, because Submitted by is same as " + entity.submittedBy);
                    isError = true;
                    return;
                }
            });
            if (isError) return;
        }
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        generalProposalVM.onShowLoading();
        generalProposalVM.onApprove(selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Approve Successfully.");
                if (searchStatus !== "") {
                    await generalProposalVM.onGroupButtonClick(searchStatus, "");
                } else {
                    await generalProposalVM.searchGeneralProposal(searchCriteria, "");
                }
                generalProposalVM.onHideLoading();
            } else {
                generalProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            generalProposalVM.onHideLoading();
        }).catch(error => {
            generalProposalVM.onHideLoading();
        });
    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, generalProposalVM, anaInfoState.operatingUnit, anaInfoState.userName]);


    const handleUnConfirm = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED" || entity.status === "PROPOSED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        generalProposalVM.onShowLoading();
        generalProposalVM.onUnconfirm(selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Unconfirm Successfully.");
                if (searchStatus !== "") {
                    await generalProposalVM.onGroupButtonClick(searchStatus, "");
                } else {
                    await generalProposalVM.searchGeneralProposal(searchCriteria, "");
                }
                generalProposalVM.onHideLoading();
            } else {
                generalProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            generalProposalVM.onHideLoading();
        }).catch(error => {
            generalProposalVM.onHideLoading();
        });

    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, generalProposalVM]);

    const handleRenew = useCallback(async () => {
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (!selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("The selected proposal is not in \"FINALIZED\" state.");
            return;
        }
        generalProposalVM.onShowLoading();
        generalProposalVM.onRenew(searchStatus, searchCriteria, selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Renewed successfully.");
                // if (searchStatus !== "") {
                //     await generalProposalVM.onGroupButtonClick(searchStatus, lastEditRowId);
                // } else {
                //     await generalProposalVM.searchGeneralProposal(searchCriteria, lastEditRowId);
                // }
                generalProposalVM.onHideLoading();
            } else {
                generalProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            generalProposalVM.onHideLoading();
        }).catch(error => {
            generalProposalVM.onHideLoading();
        });
    }, [generalProposalVM, messageBarVM, searchCriteria, searchStatus, selectedRows]);

    const handleSubmit = useCallback(async () => {
        //isSTDProposalFinalizeApproved
        if (selectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if (selectedRows.every(entity => entity.status === "FINALIZED")) {
            messageBarVM.showError("General Proposal status is \"FINALIZED\", cannot Submit.");
            return;
        }
        generalProposalVM.onShowLoading();
        generalProposalVM.onSubmit(selectedRows).then(async (data) => {
            if (data === "success") {
                messageBarVM.showSuccess("Submit Successfully.");
                if (searchStatus !== "") {
                    await generalProposalVM.onGroupButtonClick(searchStatus, "");
                } else {
                    await generalProposalVM.searchGeneralProposal(searchCriteria, "");
                }
                generalProposalVM.onHideLoading();
            } else {
                generalProposalVM.onHideLoading();
                messageBarVM.showError(data);
            }
            generalProposalVM.onHideLoading();
        }).catch(error => {
            generalProposalVM.onHideLoading();
        });

    }, [messageBarVM, searchCriteria, searchStatus, selectedRows, generalProposalVM]);

    const handleDeleteClick = useCallback(async () => {
        if (!selectedRows.every(entity => entity.status === "NEW" || entity.status === "REJECTED")) {
            messageBarVM.showError("General Proposal status is \"FINALIZED\", Cannot Delete.");
            await generalProposalVM.searchGeneralProposal(searchCriteria, "").then(data => {
                setIsLoading(false);
            });
            return;
        }
        setIsShowDelete(true);
        setDeletedProposal(selectedRows);
    }, [messageBarVM, searchCriteria, selectedRows, generalProposalVM]);

    const handleCancel = () => {
        // setIsLoading(true);
        setIsShowDelete(false);
        // generalProposalVM.searchGeneralProposal(searchCriteria).then(data => {
        //     setIsLoading(false);
        // });
    }

    const handleDelete = () => {
        setIsLoading(true);
        setIsShowDelete(false);
        if (!deletedProposal) return;

        generalProposalVM.onDeleteClick(deletedProposal).then((data) => {
            if (data && data === "success") {
                messageBarVM.showSuccess(MessageConstant.common.DELETED_DATA_SUCCESSFUL)
                generalProposalVM.searchGeneralProposal(searchCriteria, "").then(data => {
                    setIsLoading(false);
                });
            } else {
                messageBarVM.showError(data);
            }
        }).catch((error) => {
            setIsLoading(false);
        }).finally(() => {
            setIsLoading(false);
        });
    }


    const isRenewDisable = () => {
        if (!isShowEditPanel && isRead && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "FINALIZED" && entity.confirmedDate)) return false
        return true;
    }
    const isDeleteDisable = () => {
        if (!isShowEditPanel && isRead && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "NEW" || entity.status === "REJECTED")) return false
        return true;
    }
    const isSubmitDisable = () => {
        if (!isShowEditPanel && isRead && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "NEW" || entity.status === "REJECTED" || entity.status === "PROPOSED")) return false
        return true;
    }
    const isUnconfrimDisable = () => {
        if (!isShowEditPanel && isRead && selectedRows.length > 0 && selectedRows.every(entity => entity.status === "FINALIZED")) return false
        return true;
    }
    const isApproveDisable = () => {
        if (!isShowEditPanel && isRead && selectedRows.length > 0 && selectedRows.every(entity => (entity.status === "FINALIZED") && !entity.confirmedDate)) return false
        return true;
    }

    const isRejectDisable = () => {
        if (!isShowEditPanel && isRead && selectedRows.length > 0 && selectedRows.every(entity => (entity.status === "FINALIZED") && !entity.confirmedDate)) return false
        return true;
    }

    return <Sidebarheader style={{ width: '100%', display: "flex", alignItems: "center" }}>
        <HeaderTitle>{GENERAL_PROPOSAL_CONSTANT.GENERAL_PROPOSAL}</HeaderTitle>
        {(generalProposalState.isLoading || isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        <StyledAction className="tm-animated-wrapper">
            <IconButton fileName='Icon-search' disabled={isShowEditPanel} size='medium' toolTipText={'Search'} toolTipArrow={false} onClick={handleSearch} />
            {(allowCreate) && <NbisIconButton fileName='Icon-add' disabled={isShowEditPanel} size='medium' toolTipText={'Add'} onClick={handleAdd} />}
            {(allowCreate || allowDelete) && <div className="add-seperator" />}
            {(allowCreate) && <HPHButton disabled={isRenewDisable()} label={GENERAL_PROPOSAL_CONSTANT.RENEW_BUT} size={'Small'} theme={'Secondary'} onClick={handleRenew} />}
            {(allowDelete) && <HPHButton disabled={isDeleteDisable()} label={WorkspaceConstant.Common.BUTTON_DELETE} size={'Small'} theme={'Secondary'} onClick={handleDeleteClick} />}
            {(allowUpdate) && <>
                <div className="add-seperator" />
                {allowUpdateUnconfirm && <HPHButton disabled={isUnconfrimDisable()} label={WorkspaceConstant.Common.BUTTON_UNCONFIRM} size={'Small'} theme={'Secondary'} onClick={handleUnConfirm} />}
                {<HPHButton disabled={isSubmitDisable()} label={GENERAL_PROPOSAL_CONSTANT.SUBMIT_BUT} size={'Small'} theme={allowUpdate ? 'Secondary' : 'Primary'} onClick={handleSubmit} />}
                {(allowUpdateReject || allowUpdateApprove) && <div className="add-seperator" />}
                {allowUpdateReject && <HPHButton disabled={isRejectDisable()} label={GENERAL_PROPOSAL_CONSTANT.REJECT_BUT} size={'Small'} theme={'Secondary'} onClick={handleReject} />}
                {allowUpdateApprove && <HPHButton disabled={isApproveDisable()} label={GENERAL_PROPOSAL_CONSTANT.APPROVE_BUT} size={'Small'} theme={'Primary'} onClick={handleApprove} />}
            </>}
            {(allowDelete) && <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${GeneralProposalConstant.Title.GENERAL_PROPOSAL}`}
                contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
                visible={isShowDelete}
                onCancelClick={handleCancel}
                onDeleteClick={handleDelete}
            />}
        </StyledAction>
        {generalProposalState.forceRefresh && <></>}
    </Sidebarheader>
}

export default memo(GeneralProposalTitleBar);